import { DetectedWebView } from "@ciaopeople/cp-shatsu/ClientDetection/WebViewDetails.js";
import { isFunction } from "@ciaopeople/cp-shatsu/TypeChecking";
import { VideoContainerType } from "../../../model/video/VideoFormat.js";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { MediaControlOrigin } from "../../events/PlayerEvent.js";
import { VideoResource, MediaEventName, VideoRef } from "../MediaResource.js";
import { VideoPlayerElements, VideoContext, VideoPlayerControls } from "../Player.js";
import { isPlayerTechFactoryInterface, PlayerTechFactory, PlayerTechFactoryConfig, PlayerTechFactoryInterface } from "../PlayerTechFactory.js";
import { GenericFormatLabel, ParsedFormat, SizePreferencePolicy } from "./FormatAndSizePreference.js";



/**
 * Abstract factory class for creating video player technology implementations.
 * 
 * @typeParam PlayerType - The type representing the player instance.
 * 
 * @remarks
 * This class extends `PlayerTechFactory` and provides a method to build a video-specific media context,
 * which exposes various controls and properties for managing video playback, quality, audio, fullscreen,
 * and other player features.
 * 
 * @method buildPlayerMediaContext
 * Constructs a `VideoContext` object that wraps the underlying video resource and exposes
 * methods and properties for controlling playback, audio, quality, fullscreen, and more.
 * 
 * @param res - The video resource instance containing playback logic and state.
 * @param playerVideoEls - The collection of video player DOM elements.
 * @returns A `VideoContext` object for interacting with the video player.
 * 
 * @example
 * ```typescript
 * const factory = new MyVideoPlayerTechFactory();
 * const context = factory.buildPlayerMediaContext(resource, videoElements);
 * context.togglePlay(true);
 * context.switchQuality(newFormat);
 * ```
 */
export abstract class VideoPlayerTechFactory<PlayerType> extends PlayerTechFactory<PlayerType, HTMLVideoElement, VideoInfoRawData, VideoRef, VideoContext, VideoResource<PlayerType>> {

    protected buildPlayerMediaContext(res: VideoResource<PlayerType>, playerVideoEls: VideoPlayerElements): VideoContext {
        const self = this;

        const fvc: VideoContext = {
            get ended() {
                return res.ended;
            },
            reloadSource: function (keepCurrentTime): Promise<void> {
                return res.reloadSource(keepCurrentTime);
            },
            toggleFullscreen: function (fullscreen: boolean): boolean {
                return res.toggleFullscreen(fullscreen);
            },
            getAvailableQualities: function (): ParsedFormat[] {
                return res.getAvailableQualities();
            },
            reset: function (): Promise<any> {
                return res.reset();
            },
            destroy: function (): void {
                return res.destroy();
            },
            getCurrentFormat: function (): ParsedFormat {
                return res.getCurrentFormat();
            },

            togglePlay: function (manualPlay: boolean): Promise<boolean> {
                self.logger.debug("VideoClick", res);
                if (res.isPaused) {
                    res.play(manualPlay ? MediaControlOrigin.USER : MediaControlOrigin.NON_USER);
                }
                else {
                    res.pause(manualPlay ? MediaControlOrigin.USER : MediaControlOrigin.NON_USER);
                }
                return Promise.resolve(!res.isPaused);
            },
            on(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return res.on(eventName, listener);
            },
            off(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return res.off(eventName, listener);
            },
            once(eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return res.once(eventName, listener);
            },
            pause(mco: MediaControlOrigin): Promise<any> {
                return res.pause(mco);
            },
            seek(time: number): Promise<boolean> {
                return res.seek(time);
            },
            toggleAudio: function (): Promise<boolean> {
                return fvc.muted(!res.muted());
            },
            volume(volume?: number): number {
                if (!(typeof volume == "undefined")) {
                    fvc.muted(res.muted() || volume == 0);
                    res.volume(volume);
                }
                return res.volume();
            },
            muted(enable?: boolean): Promise<boolean> {
                
                const vol = res.volume();
                console.log("[AGGRO] muted enable?", enable, "volume " , vol )
                if (typeof enable !== "undefined") {
                    if (self.playerControls.getGeneralMute() != enable) {
                        self.playerControls.toggleGeneralMute();
                    }
                    if (!enable && vol == 0) {
                        res.volume(0.5);
                    }
                }
                return Promise.resolve(res.muted(enable));
            },
            unmute(): Promise<boolean> {
                return fvc.muted(false);
            },
            switchQuality(format): Promise<ParsedFormat> {
                return res.switchQuality(format);
            },
            get stats() {
                return res.stats;
            },
            get currentTime() {
                return res.currentTime;
            },
            get duration() {
                return res.duration;
            },
            get metadataPromise() {
                return res.metadataPromise;
            },
            get isPaused() {
                return res.isPaused;
            },

            beforeUnload: function (): Promise<void> {
                return res.beforeUnload();
            },
            autoplay: function (autoplay?: boolean): boolean {
                return res.autoplay(autoplay);

            },
            show: function (): void {
                return res.show();
            },
            play: function (origin: MediaControlOrigin): Promise<any> {
                return res.play(origin);
            },
            fetchLastMediaControlOrigin: function (): MediaControlOrigin {
                return res.fetchLastMediaControlOrigin();
            },
            restart: function (): void {
                res.restart();
                return;
            },
            get ref() {
                return {
                    element: playerVideoEls.mediaItem,
                    ensureResourceLoad: function () {
                        return res.ensureResourceLoad();
                    },
                    executionId: res.executionId,
                    info: res.info
                };
            }
        };
        return fvc;
    }

}
/**
 * Extends the {@link PlayerTechFactoryInterface} to provide additional methods for handling casting functionality
 * in a video player technology implementation.
 *
 * @template T - The type of the video element, typically HTMLVideoElement.
 * @template I - The type representing raw video information data.
 * @template R - The type representing the video reference.
 * @template C - The type representing the video context.
 *
 * @remarks
 * This interface adds methods to manage casting state and events, allowing implementations to respond to
 * casting updates and toggle casting mode.
 *
 * @method beforeCastUpdate
 * Called before the casting state is updated.
 * @param enable - Indicates whether casting should be enabled or disabled.
 *
 * @method onCastUpdate
 * Called when the casting state has been updated.
 * @param enable - Indicates whether casting is currently enabled or disabled.
 *
 * @method toggleCast
 * Toggles the casting state in response to a user event.
 * @param evt - The event that triggered the toggle.
 * @returns A promise that resolves when the casting state has been toggled.
 */
interface CastablePlayerTechFactoryInterface extends PlayerTechFactoryInterface<HTMLVideoElement, VideoInfoRawData, VideoRef, VideoContext> {
    beforeCastUpdate(enable: boolean): void;
    onCastUpdate(enable: boolean): void;
    toggleCast(evt: Event): Promise<void>;

}


export function isCastablePlayerTechFactory<PlayerTech>(a: any): a is CastablePlayerTechFactory<PlayerTech> {
    return ("toggleCast" in a) && isFunction(a.toggleCast) &&
        isPlayerTechFactoryInterface(a);

}

/**
 * An abstract factory class for creating video player technologies that support casting functionality,
 * such as Google Cast and AirPlay. Extends `VideoPlayerTechFactory` and implements `CastablePlayerTechFactoryInterface`.
 *
 * This class manages the switching between local and remote (cast) playback, handling platform-specific
 * logic for iOS/macOS (AirPlay) and Chrome Cast. It provides methods to toggle casting, setup remote/local players,
 * and build a media context that delegates playback controls to either the local or remote player as appropriate.
 *
 * Key responsibilities:
 * - Detects platform and manages cast session lifecycle.
 * - Handles media state synchronization between local and remote players.
 * - Provides unified media control context (`VideoContext`) that abstracts away cast/local differences.
 * - Manages event listeners for cast connection/disconnection and browser unload events.
 *
 * @template PlayerTech The type representing the underlying video player technology.
 * @extends VideoPlayerTechFactory<PlayerTech>
 * @implements CastablePlayerTechFactoryInterface
 *
 * @param onCastUpdate Callback invoked when cast state changes.
 * @param beforeCastUpdate Callback invoked before cast state changes.
 * @param playerControls Controls for the video player.
 * @param win The window object.
 * @param config Configuration for the player tech factory.
 * @param [wvDetect] Optional webview/platform detection.
 */
export abstract class CastablePlayerTechFactory<PlayerTech> extends VideoPlayerTechFactory<PlayerTech> implements CastablePlayerTechFactoryInterface {


    private resourceStateBuilder: () => { isPaused: boolean; currentTime: number; muted: boolean; };



    public toggleCast(evt: Event): Promise<void> {
        try {
            if (this._wvDetect.platform.ios || this._wvDetect.platform.macosx) {
                const videoItem = this.currentPlayerMediaElements.mediaItem;

                if (videoItem) {
                    if (videoItem["webkitShowPlaybackTargetPicker"]) {
                        videoItem["webkitShowPlaybackTargetPicker"]();
                    } else if (videoItem.remote) {
                        videoItem.remote.prompt();
                    }
                }
            }
            return Promise.resolve();
        } catch (error) {
            this.logger.error(`Error toggling cast on ios|macosx ${error}`);
            this.onCastUpdate(false);
            return Promise.resolve();
        }

    }

    private remotePlayer: any | null;
    private remotePlayerController: any | null;

    constructor(
        public onCastUpdate: (enable: boolean) => void,
        public beforeCastUpdate: (enable: boolean) => void,
        protected playerControls: VideoPlayerControls,
        protected readonly win: Window,
        public readonly config: PlayerTechFactoryConfig,
        wvDetect?: DetectedWebView) {
        super(playerControls, win, config, wvDetect);

    }

    private switchPlayer(connected: boolean) {

        if (connected) {
            this.setupRemotePlayer();
        } else {
            this.setupLocalPlayer();
        }

    }


    private get isLiveContent(): boolean {
        const currentPlayingCtx = this.currentMediaCtx;

        const currentFormat = this.currentMediaCtx.getCurrentFormat();
        if (currentFormat) {
            return currentFormat.originalDef.container != VideoContainerType.PROGRESSIVE_MP4 && currentFormat.originalDef.container != VideoContainerType.SHORT_PROGRESSIVE_MP4;
        } else {
            const source: string = currentPlayingCtx.ref.info.format.map(f => f.resourceUrl)?.[0];
            return currentPlayingCtx.ref.info.format.find((fmt) => source == fmt.resourceUrl && fmt.container != VideoContainerType.PROGRESSIVE_MP4 && fmt.container != VideoContainerType.SHORT_PROGRESSIVE_MP4
            ) != undefined;

        }

    }


    private setupRemotePlayer() {
        this.beforeCastUpdate(true);


        if (this._wvDetect?.platform?.ios || this._wvDetect?.platform?.macosx) {
            this.onCastUpdate(true);

        } else if (cast && cast.framework) {
            // Session is active
            const currentMediaCtx: VideoContext = this.currentMediaCtx;
            const playerStateBeforeSwitch = this.resourceStateBuilder();

            const source = () => {
                const currentFormat = currentMediaCtx.getCurrentFormat();
                if (this.isLiveContent) {
                    return currentMediaCtx.ref.info.format.map(f => f.resourceUrl)?.[0];
                } else {
                    const maxFormat = currentMediaCtx.getAvailableQualities().reduce((acc, value) => {
                        return (acc.mainSize > value.mainSize ? acc : value);
                    }, currentFormat);

                    return maxFormat.url;
                }
            };

            let mediaInfo = new chrome.cast.media.MediaInfo(source());
            mediaInfo.contentUrl = source();
            mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
            mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;

            mediaInfo.metadata.title = currentMediaCtx.ref.info.title;
            if (currentMediaCtx.ref.info.thumbnail)
                mediaInfo.metadata.images = [{
                    'url': currentMediaCtx.ref.info.thumbnail
                }];


            let request = new chrome.cast.media.LoadRequest(mediaInfo);
            if (this.isLiveContent) {
                // Change the streamType and add live specific metadata.
                mediaInfo.streamType = chrome.cast.media.StreamType.LIVE;
                mediaInfo.duration = -1;
                mediaInfo.contentType = 'application/x-mpegurl';
            } else {
                // Add sample breaks and breakClips.
                // mediaInfo.breakClips = breakClipsJSON;
                // mediaInfo.breaks = breaksJSON;
                mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;
                mediaInfo.contentType = 'video/mp4';
                mediaInfo.duration = currentMediaCtx.duration;
            }


            const getErrorMessage = (error) => {
                switch (error.code) {
                    case chrome.cast.ErrorCode.API_NOT_INITIALIZED:
                        return 'The API is not initialized.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.CANCEL:
                        return 'The operation was canceled by the user' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.CHANNEL_ERROR:
                        return 'A channel to the receiver is not available.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.EXTENSION_MISSING:
                        return 'The Cast extension is not available.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.INVALID_PARAMETER:
                        return 'The parameters to the operation were not valid.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.RECEIVER_UNAVAILABLE:
                        return 'No receiver was compatible with the session request.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.SESSION_ERROR:
                        return 'A session could not be created, or a session was invalid.' +
                            (error.description ? ' :' + error.description : '');
                    case chrome.cast.ErrorCode.TIMEOUT:
                        return 'The operation timed out.' +
                            (error.description ? ' :' + error.description : '');
                    default:
                        return error;
                }
            };

            const currentSession = cast.framework.CastContext.getInstance().getCurrentSession();

            currentMediaCtx.pause(MediaControlOrigin.USER)
                .then(_ => {
                    request.autoplay = !playerStateBeforeSwitch.isPaused;
                    request.currentTime = playerStateBeforeSwitch.currentTime;

                    currentMediaCtx.muted()
                        .then(muted => {
                            currentSession.setMute(muted);
                            currentSession.loadMedia(request).then((media) => {
                                this.logger.debug('Remote media loaded', media);
                                this.onCastUpdate(true);
                                // if (!playerStateBeforeSwitch.isPaused)
                                //     currentMediaCtx.play(MediaControlOrigin.USER)
                            }).catch((error) => {
                                this.logger.error('Remote media load error: ' + getErrorMessage(error));
                                this.onCastUpdate(false);
                                if (!playerStateBeforeSwitch.isPaused)
                                    currentMediaCtx.togglePlay(true);

                            });
                        });

                });
        }

    }




    private setupLocalPlayer() {
        const currentMediaCtx = this.currentMediaCtx;
        const remoteIsPaused = this.remotePlayer?.savedPlayerState?.isPaused != undefined ? this.remotePlayer?.savedPlayerState?.isPaused : currentMediaCtx.isPaused;
        const remoteIsMuted = this.remotePlayer?.savedPlayerState ? Promise.resolve(this.remotePlayer.isMuted) : currentMediaCtx.muted();
        this.onCastUpdate(false);

        if (this._wvDetect?.platform?.ios || this._wvDetect?.platform?.macosx) {
            currentMediaCtx.reloadSource(true)
                .then(_ => {
                    if (remoteIsPaused) {
                        return currentMediaCtx.play(MediaControlOrigin.USER);
                    } else {
                        return currentMediaCtx.pause(MediaControlOrigin.USER);
                    }
                });

        } else {
            const time = this.remotePlayer && this.remotePlayer.savedPlayerState && this.remotePlayer.savedPlayerState?.currentTime ? this.remotePlayer.savedPlayerState?.currentTime : currentMediaCtx.currentTime;

            currentMediaCtx.seek(time)
                .then(_ => {
                    return remoteIsMuted.then(muted => {
                        return this.currentMediaCtx.muted(muted);
                    });
                })

                .then(_ => {
                    if (remoteIsPaused) {
                        return currentMediaCtx.pause(MediaControlOrigin.USER);
                    } else {
                        return currentMediaCtx.play(MediaControlOrigin.USER);
                    }
                });


        }

    }


    protected buildPlayerMediaContext(res: VideoResource<PlayerTech>, playerVideoEls: VideoPlayerElements): VideoContext {

        const originalControls = super.buildPlayerMediaContext(res, playerVideoEls);

        const self = this;

        const ctx: VideoContext = {
            pause(mc: MediaControlOrigin): Promise<void> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {
                    if (!self.remotePlayer?.isPaused) {
                        return self.remotePlayerController.playOrPause();
                    }
                } else {
                    return originalControls.pause(mc);
                }
            },
            play(mc: MediaControlOrigin): Promise<void> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {
                    if (self.remotePlayer?.isPaused) {
                        return self.remotePlayerController.playOrPause();
                    }
                } else {
                    return originalControls.play(mc);
                }
            },


            togglePlay(manualPlay: boolean): Promise<boolean> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {
                    self.remotePlayerController.playOrPause();
                    return Promise.resolve(!self.remotePlayer?.isPaused);
                } else return originalControls.togglePlay(manualPlay);
            },
            seek(time: number): Promise<boolean> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {

                    self.remotePlayer.currentTime = time;

                    self.remotePlayerController.seek();
                    return Promise.resolve(true);
                } else return originalControls.seek(time);
            },
            get currentTime() {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayerController != null && self.remotePlayer?.currentTime >= originalControls.currentTime) {
                    return self.remotePlayer.currentTime;
                } else {
                    return originalControls.currentTime;
                }

            },
            toggleAudio(): Promise<boolean> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayerController != null) {
                    self.remotePlayerController.muteOrUnmute();
                    return Promise.resolve(self.remotePlayer?.isMuted);
                }
                return originalControls.toggleAudio();
            },

            unmute(): Promise<boolean> {
                return ctx.muted(false);
            },

            muted(enable?: boolean): Promise<boolean> {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {
                    if (typeof enable != "undefined" && self.remotePlayer?.isMuted != enable) {
                        self.remotePlayerController.muteOrUnmute();
                    }
                    return Promise.resolve(self.remotePlayer?.isMuted);
                } else {
                    return originalControls.muted(enable);
                }
            },

            volume(volume?: number): number {
                if (self.remotePlayer != null && self.remotePlayer?.isConnected && self.remotePlayer?.isMediaLoaded && self.remotePlayerController != null) {
                    if (typeof volume != "undefined") {
                        self.remotePlayer.volumeLevel = volume;
                        self.remotePlayerController.setVolume();
                    }
                    return self.remotePlayer.volumeLevel;

                } else return originalControls.volume(volume);
            },
            toggleFullscreen: function (fullscreen: boolean): boolean {
                return originalControls.toggleFullscreen(fullscreen)
            },
            getCurrentFormat: function (): ParsedFormat {
                return originalControls.getCurrentFormat();
            },
            getAvailableQualities: function (): ParsedFormat[] {
                return originalControls.getAvailableQualities();
            },
            switchQuality: function (format: GenericFormatLabel): Promise<ParsedFormat> {
                return originalControls.switchQuality(format);
            },
            get ended() {
                return originalControls.ended;
            },
            get stats() {
                return originalControls.stats
            },
            get duration() { return originalControls.duration },
            get metadataPromise() { return originalControls.metadataPromise },
            get isPaused() { return originalControls.isPaused },
            get ref() { return originalControls.ref},
            reset: function (): Promise<any> {
                return originalControls.reset()
            },
            destroy: function (): void {
                return originalControls.destroy();
            },
            beforeUnload: function (): Promise<void> {
                return originalControls.beforeUnload();
            },
            autoplay: function (autoplay?: boolean): boolean {
                return originalControls.autoplay(autoplay)
            },
            show: function (): void {
                return originalControls.show()
            },
            fetchLastMediaControlOrigin: function (): MediaControlOrigin {
                return originalControls.fetchLastMediaControlOrigin()
            },
            restart: function (): void {
                return originalControls.restart()
            },
            reloadSource: function (keepCurrentTime: boolean): Promise<void> {
                return originalControls.reloadSource(keepCurrentTime)
            },
            on: function (eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return originalControls.on(eventName, listener)
            },
            off: function (eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return originalControls.off(eventName, listener)
            },
            once: function (eventName: MediaEventName, listener: EventListenerOrEventListenerObject): void {
                return originalControls.once(eventName, listener)
            }
        };

        this.handleConnectListener(res.element, ctx);


        this.resourceStateBuilder = () => {
            return {
                isPaused: res.isPaused,
                muted: res.muted(),
                currentTime: res.currentTime
            };
        };

        return ctx;

    }

    private handleConnectListener(videoEl: HTMLVideoElement, ctx: VideoContext) {

        if (this._wvDetect?.platform.ios || this._wvDetect?.platform.macosx) {

            const onconnect = (event) => {
                this.switchPlayer(true);
            };

            const ondisconnect = (event) => {
                this.switchPlayer(false);
            };

            videoEl.remote.onconnect = null;
            videoEl.remote.onconnect = onconnect;

            videoEl.remote.ondisconnect = null;
            videoEl.remote.ondisconnect = ondisconnect;
        } else {
            const beforeUnload = (evt) => {
                if (this.win["castAvailable"] && cast && cast.framework && cast.framework.CastContext.getInstance().getCurrentSession()) {
                    cast.framework.CastContext.getInstance().getCurrentSession().endSession(true);
                }
            };

            this.win.removeEventListener("beforeunload", beforeUnload);
            this.win.addEventListener("beforeunload", beforeUnload);

            this.win['__onGCastApiAvailable'] = (isAvailable) => {
                if (isAvailable) {
                    const options = {};

                    this.win["castAvailable"] = true;
                    options["receiverApplicationId"] = "CC1AD845";

                    // Auto join policy can be one of the following three:
                    // ORIGIN_SCOPED - Auto connect from same appId and page origin
                    // TAB_AND_ORIGIN_SCOPED - Auto connect from same appId, page origin, and tab
                    // PAGE_SCOPED - No auto connect
                    options["autoJoinPolicy"] = chrome.cast.AutoJoinPolicy.PAGE_SCOPED;

                    /** The following flag enables Cast Connect(requires Chrome 87 or higher) */
                    options["androidReceiverCompatible"] = true;

                    cast.framework.CastContext.getInstance().setOptions(options);

                    this.remotePlayer = new cast.framework.RemotePlayer();


                    const onConnectChange = (e) => {
                        this.switchPlayer(e.value);
                    };

                    if (this.remotePlayerController) {
                        this.remotePlayerController.removeEventListener(
                            cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
                            onConnectChange
                        );
                    }

                    this.remotePlayerController = new cast.framework.RemotePlayerController(this.remotePlayer);

                    this.remotePlayerController?.addEventListener(
                        cast.framework.RemotePlayerEventType.IS_CONNECTED_CHANGED,
                        onConnectChange
                    );


                }
            };
        }

    }

}



interface PlayerTechFactoryInterfaceWithSizePreferencePolicy {
    setSizePreferencePolicy(size: SizePreferencePolicy): void;
}

export abstract class PlayerTechFactoryWithSizePreferencePolicy<PlayerTech> extends VideoPlayerTechFactory<PlayerTech> implements PlayerTechFactoryInterfaceWithSizePreferencePolicy {
    readonly abstract config: PlayerTechFactoryConfig;

    public abstract setSizePreferencePolicy(size: SizePreferencePolicy): void;
}
